import React, { useEffect, useState } from "react";
import { X, CheckCircle2, Sparkles } from "lucide-react";

/**
 * iter65 — Version Compare modal.
 *
 * Lists every version of a (shot, target) chain. Exactly one version
 * may be approved at a time; clicking "Approve this version" demotes
 * any previously-approved version.
 *
 * Props:
 *   - api               — axios-style instance (parent supplies)
 *   - open / onClose
 *   - projectId, target ("storyboard" | "video"), shotId
 *   - onApproved(version_number) — called after a successful approve
 */
export default function VersionCompareModal({
    open, api, projectId, target, shotId, onApproved, onClose,
}) {
    const [versions, setVersions] = useState([]);
    const [approved, setApproved] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    // iter66.1 — surface the QC routing payload returned by the approve
    // endpoint (only set for `target=video`). Lets the parent shot card
    // flip its QC chip from vN-1 → vN in real time, without waiting for
    // the next /projects/{id} reload.
    const [lastQcReview, setLastQcReview] = useState(null);

    useEffect(() => {
        if (!open || !projectId || !shotId) return;
        let cancelled = false;
        setLastQcReview(null);   // iter66.1 — fresh QC payload per open
        (async () => {
            setError("");
            try {
                const r = await api.get(
                    `/projects/${projectId}/regeneration-versions`,
                    { params: { target, shot_id: shotId } },
                );
                if (cancelled) return;
                setVersions(r.data?.versions || []);
                setApproved(r.data?.approved_version ?? null);
            } catch (e) {
                if (cancelled) return;
                setError(e?.response?.data?.detail?.error || "load_failed");
            }
        })();
        return () => { cancelled = true; };
    }, [open, projectId, target, shotId, api]);

    if (!open) return null;

    const approve = async (vn) => {
        setBusy(true);
        setError("");
        try {
            const r = await api.post(
                `/projects/${projectId}/regeneration-versions/approve`,
                { target, shot_id: shotId, version_number: vn },
            );
            setVersions(r.data?.versions || []);
            setApproved(r.data?.approved_version ?? vn);
            // iter66.1 — capture the qc_review payload (video target
            // only; storyboard approval intentionally does NOT emit a
            // qc_reviews row yet — deferred to iter67).
            const qc = r.data?.qc_review || null;
            setLastQcReview(qc);
            onApproved?.(vn, qc);
        } catch (e) {
            setError(e?.response?.data?.detail?.error || "approve_failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto"
            data-testid={`version-compare-modal-${target}-${shotId}`}
        >
            <div className="bg-zinc-950 border border-zinc-800 rounded-md w-full max-w-3xl p-5 m-6 space-y-4">
                <div className="flex items-start justify-between">
                    <div>
                        <h3 className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-amber-300" />
                            Compare versions — {target} · <span className="font-mono">{shotId}</span>
                        </h3>
                        <p className="text-[11px] text-zinc-500 mt-1">
                            Approving a version sends it to QC and demotes any
                            previously-approved version.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {error && (
                    <div className="text-[11px] text-rose-300 bg-rose-950/30 border border-rose-700/40 rounded-md px-3 py-2">
                        {error}
                    </div>
                )}

                {lastQcReview && target === "video" && (
                    <div
                        data-testid={`version-compare-qc-confirmation-${shotId}`}
                        className="text-[11px] text-emerald-200 bg-emerald-950/30 border border-emerald-700/40 rounded-md px-3 py-2 flex items-center gap-2"
                    >
                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                        <span>
                            QC routed to{" "}
                            <span className="font-mono font-semibold">
                                v{lastQcReview.selected_version_number}
                            </span>
                            {" · "}
                            <span className="text-emerald-300/80">
                                asset {String(lastQcReview.asset_id || "").slice(0, 8)}…
                            </span>
                        </span>
                    </div>
                )}

                {versions.length === 0 ? (
                    <div className="text-[12px] text-zinc-500 py-6 text-center">
                        No regenerated versions yet. v1 is the original.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {versions.map((v) => (
                            <div
                                key={v.id}
                                data-testid={`version-row-${target}-${shotId}-v${v.version_number}`}
                                className={`rounded-md border p-3 ${
                                    v.version_number === approved
                                        ? "border-emerald-700/50 bg-emerald-950/20"
                                        : "border-zinc-800 bg-zinc-950/60"
                                }`}
                            >
                                <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="text-xs font-semibold text-zinc-100 flex items-center gap-2">
                                            v{v.version_number}
                                            {v.is_original && (
                                                <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                                                    original
                                                </span>
                                            )}
                                            {v.version_number === approved && (
                                                <span className="text-[10px] uppercase tracking-wider text-emerald-300 inline-flex items-center gap-1">
                                                    <CheckCircle2 className="w-3 h-3" /> approved (QC)
                                                </span>
                                            )}
                                            <span className={`text-[10px] uppercase tracking-wider ${
                                                v.status === "succeeded" ? "text-zinc-400"
                                                    : v.status === "failed" ? "text-rose-400"
                                                    : "text-amber-300"
                                            }`}>
                                                {v.status}
                                            </span>
                                        </div>
                                        {v.director_summary && (
                                            <div
                                                data-testid={`version-summary-${target}-${shotId}-v${v.version_number}`}
                                                className="text-[11px] text-zinc-300 mt-1.5 leading-snug"
                                            >
                                                <span className="text-zinc-500">summary:</span>{" "}
                                                {v.director_summary}
                                            </div>
                                        )}
                                        {v.raw_director_note && (
                                            <div className="text-[11px] text-zinc-400 mt-1 truncate">
                                                <span className="text-zinc-500">director note:</span>{" "}
                                                {v.raw_director_note}
                                            </div>
                                        )}
                                        {(v.change_list || []).length > 0 && (
                                            <div className="text-[11px] text-zinc-400 mt-1">
                                                <span className="text-zinc-500">change:</span>{" "}
                                                {v.change_list.slice(0, 3).join(" · ")}
                                            </div>
                                        )}
                                        {(v.warnings || []).length > 0 && (
                                            <div className="text-[11px] text-amber-300 mt-1">
                                                {v.warnings.slice(0, 2).map((w, i) => <div key={i}>⚠ {w}</div>)}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        data-testid={`approve-version-${target}-${shotId}-v${v.version_number}`}
                                        onClick={() => approve(v.version_number)}
                                        disabled={busy || v.version_number === approved || v.status !== "succeeded"}
                                        className="shrink-0 text-[11px] px-2.5 py-1 rounded-sm border border-emerald-700/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40"
                                    >
                                        {v.version_number === approved ? "Approved" : "Approve this version"}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex items-center justify-end pt-2 border-t border-zinc-800">
                    <button
                        onClick={onClose}
                        className="text-xs px-3 py-1.5 text-zinc-400 hover:text-zinc-200"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
